/**
 * clave-primer-ingreso.js — La contraseña temporal se cambia antes de entrar.
 *
 * Vivía dentro de `page-profile.js` porque nació ahí, junto al botón «Cambiar
 * contraseña» de Mi perfil. Cuando esa pantalla se migró a React (2026-09-03)
 * el botón se fue con ella, pero esta función se quedó — y el portal siguió
 * descargando y evaluando las 538 líneas del archivo entero, en cada visita,
 * para poder llamar a una.
 *
 * No se movió a React a propósito, y la decisión sigue en pie: esto es una
 * **barrera del arranque de sesión**, no una acción de una pantalla. Se dispara
 * desde `startApp()` antes de que el colaborador llegue a ninguna parte, y
 * atarla a que un árbol de React haya montado bien pondría el primer inicio de
 * sesión de toda la organización detrás de una dependencia que no necesita.
 *
 * Su sitio es el shell. Aquí está.
 *
 * El equivalente en React —`features/perfil/CambiarContrasena.tsx`— cubre el
 * OTRO caso: el cambio voluntario desde Mi perfil, con validación al salir de
 * cada campo. Los dos hablan con `POST /auth/change-password` y no comparten
 * nada más; no son una duplicación, son dos momentos distintos.
 */
const ClavePrimerIngreso = (() => {

  /**
   * Abre el diálogo del cambio obligatorio.
   *
   * No lleva botón de cancelar: el candado del backend
   * (`app/core/auth.py::_block_if_must_change`) va a responder 403 a todo lo
   * demás mientras `must_change_password` siga puesto, así que ofrecer una
   * salida sería ofrecer un callejón.
   */
  function abrir() {
    Utils.modal({
      title: '<i class="bi bi-key"></i> Cambiar contraseña',
      body: `
        <div class="alert alert-warning" style="font-size:var(--fs-3)">
          <i class="bi bi-shield-exclamation"></i>
          Por seguridad debes definir una contraseña nueva antes de continuar.
          Ingresa la contraseña temporal que te entregaron como «contraseña actual».
        </div>
        <div class="form-group"><label class="form-label" for="cpi-actual">Contraseña actual *</label>
          <input type="password" class="form-input" id="cpi-actual" autocomplete="current-password" /></div>
        <div class="form-group"><label class="form-label" for="cpi-nueva">Contraseña nueva *
            <span class="form-hint">(mínimo 8 caracteres)</span></label>
          <input type="password" class="form-input" id="cpi-nueva" autocomplete="new-password" /></div>
        <div class="form-group"><label class="form-label" for="cpi-confirmar">Confirmar contraseña nueva *</label>
          <input type="password" class="form-input" id="cpi-confirmar" autocomplete="new-password" /></div>`,
      footer: '<button class="btn btn-primary" onclick="ClavePrimerIngreso.guardar()">Cambiar</button>',
    });
  }

  async function guardar() {
    const actual    = document.getElementById('cpi-actual').value;
    const nueva     = document.getElementById('cpi-nueva').value;
    const confirmar = document.getElementById('cpi-confirmar').value;
    if (!actual || !nueva)   { Utils.toast('Completa todos los campos', 'error'); return; }
    if (nueva !== confirmar) { Utils.toast('Las contraseñas no coinciden', 'error'); return; }
    if (nueva.length < 8)    { Utils.toast('La contraseña debe tener mínimo 8 caracteres', 'error'); return; }
    try {
      await API.post('/auth/change-password', { current_password: actual, new_password: nueva });
      Utils.closeModal();
      Utils.toast('Contraseña actualizada exitosamente', 'success');
      // El arranque se reanuda desde el principio: `startApp()` volvió a salir
      // por aquí antes de llegar al gate de Habeas Data y al de datos
      // iniciales, así que hay que devolverle el turno.
      const u = Auth.getUser();
      if (u?.must_change_password) {
        u.must_change_password = false;
        localStorage.setItem('user', JSON.stringify(u));
        if (window.APP?.startApp) APP.startApp();
      }
    } catch (err) { Utils.toast(err.message, 'error'); }
  }

  return { abrir, guardar };
})();
window.ClavePrimerIngreso = ClavePrimerIngreso;
